import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import Title from "../ui/Title";
import { useBmapi } from "../utils/bmapi-context";
import { useIntl } from "react-intl";
import { getErrorMessageString } from "../utils/errors";
import {
  ArrowBack,
  ArrowDownward,
  ArrowUpward,
  FiberManualRecord,
} from "@material-ui/icons";
import { common } from "../messages";
import utils, { parseBmeIntToInt } from "../utils/utils";
import { INIT_QUERY } from "../utils/gdspConstants";
import { format, subMonths, startOfDay } from "date-fns";
import { MANAGER_ROUTES } from "../utils/constants";
import { useHistory } from "react-router-dom";
import Confirm from "../ui/Confirm";
import ActionsDetails from "./ActionsDetails";
import PrizesDetails from "./PrizesDetails";
import { DatePicker } from "../ui/DatePicker";

const ProvinceList = lazy(() =>
  import(/* webpackChunkName: 'ProvinceList' */ "./ProvinceList.js")
);

export default function UsersStatistics() {
  const initialOrder = useMemo(
    () => ({
      status: "",
      email: "",
      issued: "",
      used: "",
    }),
    []
  );

  const intl = useIntl();
  const history = useHistory();
  const { bmapi, notifyError, startLoading, stopLoading } = useBmapi();

  // default "Da" = 3 mesi fa (inizio giornata)
  const defaultFrom = React.useMemo(
    () => startOfDay(subMonths(new Date(), 1)),
    []
  );

  // STATE
  const [users, setUsers] = useState([]);
  const [actions, setActions] = useState([]);
  const [userPrizes, setUserPrizes] = useState([]);
  const [userSelected, setUserSelected] = useState([]);
  const [province, setProvince] = useState([]);
  const [query, setQuery] = useState(() => ({
    ...INIT_QUERY,
    from_created_at: format(defaultFrom, "yyyy-MM-dd"),
  }));
  const [count, setCount] = useState(0);
  const [email, setEmail] = useState("");
  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(null);
  const [showData, setShowData] = useState(false);
  const [disableUser, setDisableUser] = React.useState(false);
  const [ordered, setOrdered] = useState(initialOrder);

  const changeStatusLabel =
    userSelected.status == 0 ? "Disabilita Utente" : "Abilita Utente";
  const changeStatusMessage =
    userSelected.status == 0 ? "disabilitare" : "abilitare";
  const action = userSelected.status == 0 ? "DISABLE" : "ENABLE";

  const goToHome = () => {
    history.push(MANAGER_ROUTES.HOME_MENU);
  };

  const getUsers = useCallback(
    (q) => {
      startLoading();
      if (bmapi) {
        bmapi
          .getUserStatsWithPoints(q || query)
          .then((resp) => {
            resp.map((r) => {
              r.user.custom_fields = JSON.parse(r.user.custom_fields);
              return r;
            });
            setUsers(resp);
            return bmapi.getUserStatsWithPointsCount(q || query);
          })
          .then((resp) => {
            setCount(resp);
            stopLoading();
          })
          .catch((e) => {
            notifyError(getErrorMessageString(e, intl));
            stopLoading();
          });
      }
    },
    [bmapi, intl, notifyError, startLoading, stopLoading, query]
  );

  useEffect(() => {
    // parte già con from_created_at = defaultFrom (3 mesi fa)
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeStatus = (action, user) => {
    startLoading();

    if (action === "ENABLE") {
      bmapi
        .enableUser(user, "gdsp-x-bme")
        .then(() => {
          handleClose();
          getUsers();
        })
        .catch((e) => {
          notifyError(getErrorMessageString(e, intl));
        });
    } else {
      bmapi
        .disableUser(user, "gdsp-x-bme")
        .then(() => {
          handleClose();
          getUsers();
        })
        .catch((e) => {
          notifyError(getErrorMessageString(e, intl));
        });
    }

    stopLoading();
  };

  const search = () => {
    email.trim();
    if (!dateFrom) {
      notifyError(
        intl.formatMessage({
          id: "errors.requiredDateFrom",
          defaultMessage: "La data di inizio è obbligatoria.",
        })
      );
      return;
    }
    const params = {
      ...query,
      email: email || null,
      prov: province || null,
      from_created_at: format(dateFrom, "yyyy-MM-dd"),
      to_created_at: dateTo ? format(dateTo, "yyyy-MM-dd") : null,
      page: 0, // reset paginazione quando si applicano nuovi filtri
    };
    for (const property in params) {
      if (params[property] === null) {
        delete params[property];
      }
    }
    setQuery(params);
    getUsers(params);
  };

  const download = () => {
    startLoading();
    bmapi
      .getUserStats()
      .then((blob) => utils.download(blob, "export.csv"))
      .catch((e) => notifyError(getErrorMessageString(e, intl)))
      .finally(stopLoading);
  };

  const handleChangeOrderBy = useCallback(
    (value, varName) => {
      setOrdered(initialOrder);
      setOrdered((v) => ({
        ...v,
        [varName]: value.startsWith("-") ? "minor" : "major",
      }));
      // conserva i filtri correnti
      const next = { ...query, orderBy: value, page: 0 };
      setQuery(next);
      getUsers(next);
    },
    [initialOrder, query, getUsers]
  );

  const handleChangePage = (goTop) => (event, newPage) => {
    const next = { ...query, page: newPage };
    setQuery(next);
    getUsers(next);
    if (goTop) window.scrollTo(0, 0);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    const next = { ...query, qty: newRowsPerPage, page: 0 };
    setQuery(next);
    getUsers(next);
  };

  const getWelcomeRow = (createdAt) => {
    const row = {
      action_date: createdAt,
      business_name: "SposiClub.it",
      created_at: createdAt,
      id: "welcome-0000",
      status: "CONFIRMED",
      type: "GIFT",
      reward: 1000,
    };
    return row;
  };

  const getCompleteProfileRow = (user) => {
    const row = {
      action_date: user.created_at,
      business_name: "SposiClub.it",
      created_at: user.created_at,
      id: "profile-0000",
      status: "CONFIRMED",
      type: "PROFILE50",
      reward: 5000,
    };
    return row;
  };

  const showActions = (user) => {
    startLoading();
    setShowData(true);
    setUserSelected(user);

    bmapi
      .getAllRewardingActions({ user: user.id })
      .then((act) => {
        if (!act) act = [];
        const customFields = user.custom_fields;
        const profileComplete =
          customFields.profile_complete != null &&
          customFields.profile_complete !== ""
            ? customFields.profile_complete
            : false;
        if (profileComplete) {
          act.push(getCompleteProfileRow(user));
        }

        act.push(getWelcomeRow(user.created_at));
        setActions(act);

        return bmapi.getUserInspectionProducts("", { user: user.id });
      })
      .then((products) => {
        const prizes = (products || []).filter((p) => !p.default);
        setUserPrizes(prizes);
        stopLoading();
      })
      .catch((e) => {
        notifyError(getErrorMessageString(e, intl));
        stopLoading();
      });
  };

  const handleClose = () => {
    setShowData(false);
    setDisableUser(false);
    setActions([]);
    setUserPrizes([]);
  };

  return (
    <React.Fragment>
      <Title>Statistiche Utenti</Title>

      {/* FILTRI */}
      <Container maxWidth="sm">
        <TextField
          label={intl.formatMessage(common.filterEmail)}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          name="email"
          fullWidth
        />

        <Suspense fallback={<div>...{intl.formatMessage(common.loading)}</div>}>
          <ProvinceList
            province={province}
            handleValue={(e) => setProvince(e.target.value)}
            label="Filtra per provincia"
            name="competence_locations"
            margin="dense"
            noProvince={true}
          />
        </Suspense>

        <div style={{ display: "flex" }}>
          <DatePicker
            label={intl.formatMessage(common.filterFrom)}
            value={dateFrom}
            onChange={(e) => e && setDateFrom(e)} // impedisce null
            fullWidth
            margin="dense"
            required
            clearable={false}
          />
          <DatePicker
            label={intl.formatMessage(common.filterTo)}
            value={dateTo}
            onChange={(e) => setDateTo(e)}
            fullWidth
            margin="dense"
            minDate={dateFrom}
            clearable
          />
        </div>

        <Box mt={2}>
          <Button variant="contained" color="primary" onClick={search}>
            {intl.formatMessage(common.search)}
          </Button>
        </Box>
      </Container>

      <Box my={2}>
        <Button onClick={goToHome} startIcon={<ArrowBack />}>
          {intl.formatMessage(common.backHome)}
        </Button>
      </Box>

      {/* TABELLA */}
      <Card>
        <CardContent>
          <Box mb={5} mt={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={download}
              style={{ float: "right" }}
            >
              Scarica utenti
            </Button>
            <FiberManualRecord
              fontSize="small"
              style={{ color: "green", marginLeft: 25 }}
            />
            <Typography variant="caption">Utenti attivi</Typography>
            <FiberManualRecord
              fontSize="small"
              style={{ color: "red", marginLeft: 25 }}
            />
            <Typography variant="caption">Utenti disabilitati</Typography>
          </Box>

          <TableContainer>
            <Table aria-label="simple table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    onClick={() =>
                      handleChangeOrderBy(
                        ordered.status === "minor" ? "status" : "-status",
                        "status"
                      )
                    }
                    style={{ cursor: "pointer" }}
                    hover
                  >
                    <Typography
                      variant="body2"
                      style={{
                        fontWeight: "bold",
                        textDecoration: "underline",
                      }}
                    >
                      Stato
                    </Typography>
                    {ordered.status && (
                      <IconButton style={{ padding: 0 }}>
                        {ordered.status === "minor" ? (
                          <ArrowDownward fontSize="small" />
                        ) : (
                          <ArrowUpward fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell
                    onClick={() =>
                      handleChangeOrderBy(
                        ordered.email === "minor" ? "email" : "-email",
                        "email"
                      )
                    }
                    style={{ cursor: "pointer" }}
                    hover
                  >
                    <Typography
                      variant="body2"
                      style={{
                        fontWeight: "bold",
                        textDecoration: "underline",
                      }}
                    >
                      Email
                    </Typography>
                    {ordered.email && (
                      <IconButton style={{ padding: 0 }}>
                        {ordered.email === "minor" ? (
                          <ArrowDownward fontSize="small" />
                        ) : (
                          <ArrowUpward fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>Nome e cognome</TableCell>
                  <TableCell
                    onClick={() =>
                      handleChangeOrderBy(
                        ordered.issued === "minor"
                          ? "issuedValue"
                          : "-issuedValue",
                        "issued"
                      )
                    }
                    style={{ cursor: "pointer" }}
                    hover
                  >
                    <Typography
                      variant="body2"
                      style={{
                        fontWeight: "bold",
                        textDecoration: "underline",
                      }}
                    >
                      P.TI ricevuti
                    </Typography>
                    {ordered.issued && (
                      <IconButton style={{ padding: 0 }}>
                        {ordered.issued === "minor" ? (
                          <ArrowDownward fontSize="small" />
                        ) : (
                          <ArrowUpward fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell
                    onClick={() =>
                      handleChangeOrderBy(
                        ordered.used === "minor" ? "usedValue" : "-usedValue",
                        "used"
                      )
                    }
                    style={{ cursor: "pointer" }}
                    hover
                  >
                    <Typography
                      variant="body2"
                      style={{
                        fontWeight: "bold",
                        textDecoration: "underline",
                      }}
                    >
                      P.TI usati/cancellati
                    </Typography>
                    {ordered.used && (
                      <IconButton style={{ padding: 0 }}>
                        {ordered.used === "minor" ? (
                          <ArrowDownward fontSize="small" />
                        ) : (
                          <ArrowUpward fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>Provincia</TableCell>
                  <TableCell>Data matrimonio</TableCell>
                  <TableCell>Data registrazione</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {users.map((u) => (
                  <TableRow
                    key={u.user.id}
                    onClick={() => showActions(u.user)}
                    style={{ cursor: "pointer" }}
                    hover
                  >
                    <TableCell>
                      <FiberManualRecord
                        style={{
                          color: u.user.status !== 2 ? "green" : "red",
                        }}
                      />
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {u.user.email || "-"}
                    </TableCell>
                    <TableCell>
                      {u.user.complete_name
                        ? u.user.complete_name + " " + u.user.last_name
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {parseBmeIntToInt(u.performance.issued_value)}
                    </TableCell>
                    <TableCell>
                      {parseBmeIntToInt(u.performance.used_value)}
                    </TableCell>
                    <TableCell>
                      {u.user.custom_fields.wedding_province || "-"}
                    </TableCell>
                    <TableCell>
                      {u.user.custom_fields.wedding_date
                        ? format(
                            new Date(u.user.custom_fields.wedding_date),
                            "dd-MM-yyyy"
                          )
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {u.user.created_at
                        ? format(new Date(u.user.created_at), "dd-MM-yyyy")
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={count}
              rowsPerPage={query.qty}
              page={query.page}
              onChangePage={handleChangePage(true)}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </TableContainer>
        </CardContent>
      </Card>

      <Box my={2}>
        <Button onClick={goToHome} startIcon={<ArrowBack />}>
          {intl.formatMessage(common.backHome)}
        </Button>
      </Box>

      {/* DIALOG UTENTE */}
      <Dialog open={showData} onClose={handleClose} fullWidth maxWidth="md">
        <DialogContent>
          <Box mb={3}>
            {userSelected.picture ? (
              <CardMedia
                image={userSelected.picture}
                style={{
                  height: "100px",
                  width: "100px",
                  margin: "0 auto",
                }}
              />
            ) : (
              <Avatar
                style={{
                  height: "100px",
                  width: "100px",
                  margin: "0 auto",
                }}
              />
            )}
            <Typography align="center">
              {`${userSelected.complete_name} ${userSelected.last_name}`}
            </Typography>
            <Typography align="center">{`(${userSelected.email})`}</Typography>
            <Button
              style={{ display: "block", margin: "20px auto" }}
              variant="contained"
              color="primary"
              onClick={() => setDisableUser(true)}
            >
              {changeStatusLabel}
            </Button>
          </Box>

          <PrizesDetails prizes={userPrizes} />
          <ActionsDetails showQRCode={null} title={null} actions={actions} />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={handleClose}>
            {intl.formatMessage(common.close)}
          </Button>
        </DialogActions>
      </Dialog>

      {disableUser && (
        <Confirm
          open={!!disableUser}
          onConfirm={() => changeStatus(action, userSelected.id)}
          onCancel={() => setDisableUser(false)}
          text={
            "Vuoi " +
            changeStatusMessage +
            " l'accesso e l'utilizzo dell'applicazione all'utente " +
            userSelected.email +
            "?"
          }
        />
      )}
    </React.Fragment>
  );
}
